import React from 'react';
import { SectionCard, TextField, TextAreaField, ImageField, Repeater } from '../../../components/admin/EditorFields';

export default function ImpactEditor({ data, setData }) {
  const upd = (path, value) => setData((d) => {
    const next = JSON.parse(JSON.stringify(d || {}));
    const keys = path.split('.');
    let cur = next;
    for (let i = 0; i < keys.length - 1; i++) {
      cur[keys[i]] = cur[keys[i]] || {};
      cur = cur[keys[i]];
    }
    cur[keys[keys.length - 1]] = value;
    return next;
  });

  return (
    <div className="space-y-5">
      {/* HERO */}
      <SectionCard title="Hero (en-tête)" description="La bannière principale de la page Impact écologique.">
        <TextField label="Eyebrow (badge au-dessus)" value={data.hero?.eyebrow} onChange={(v) => upd('hero.eyebrow', v)} />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Titre" value={data.hero?.title} onChange={(v) => upd('hero.title', v)} />
          <TextField label="Titre en couleur" value={data.hero?.titleHighlight} onChange={(v) => upd('hero.titleHighlight', v)} />
        </div>
        <TextAreaField label="Description" rows={3} value={data.hero?.description} onChange={(v) => upd('hero.description', v)} />
      </SectionCard>

      {/* INTRO */}
      <SectionCard title="Introduction" description="Paragraphes situés sous les 3 cartes de synthèse.">
        <Repeater
          label="Paragraphes"
          items={data.intro?.paragraphs}
          onChange={(items) => upd('intro.paragraphs', items)}
          newItem={() => ''}
          renderItem={(item, update, i) => (
            <TextAreaField
              label={`Paragraphe ${i + 1}`}
              rows={3}
              value={item}
              onChange={(v) => setData((d) => {
                const next = JSON.parse(JSON.stringify(d));
                next.intro.paragraphs[i] = v;
                return next;
              })}
            />
          )}
        />
      </SectionCard>

      {/* DANGERS - MICROPLASTIQUES */}
      <SectionCard title="Dangers — Microplastiques" description="Section rose « Alerte santé ».">
        <TextField label="Titre" value={data.dangers?.title} onChange={(v) => upd('dangers.title', v)} />
        <Repeater
          label="Paragraphes (HTML autorisé)"
          items={data.dangers?.paragraphs}
          onChange={(items) => upd('dangers.paragraphs', items)}
          newItem={() => ''}
          renderItem={(item, update, i) => (
            <TextAreaField
              label={`Paragraphe ${i + 1}`}
              rows={4}
              value={item}
              onChange={(v) => setData((d) => {
                const next = JSON.parse(JSON.stringify(d));
                next.dangers.paragraphs[i] = v;
                return next;
              })}
            />
          )}
        />
      </SectionCard>

      {/* PETROL - FABRICATION */}
      <SectionCard title="Pétrole & fabrication" description="Section avec 2 colonnes (annuel / fabrication PET).">
        <TextField label="Titre" value={data.petrol?.title} onChange={(v) => upd('petrol.title', v)} />
        <TextAreaField label="Introduction" rows={2} value={data.petrol?.intro} onChange={(v) => upd('petrol.intro', v)} />

        <Repeater
          label="Chaque année (colonne gauche)"
          items={data.petrol?.yearly}
          onChange={(items) => upd('petrol.yearly', items)}
          newItem={() => ''}
          renderItem={(item, update, i) => (
            <TextField
              label={`Item ${i + 1}`}
              value={item}
              onChange={(v) => setData((d) => {
                const next = JSON.parse(JSON.stringify(d));
                next.petrol.yearly[i] = v;
                return next;
              })}
            />
          )}
        />

        <Repeater
          label="Fabrication PET (colonne droite)"
          items={data.petrol?.manufacture}
          onChange={(items) => upd('petrol.manufacture', items)}
          newItem={() => ''}
          renderItem={(item, update, i) => (
            <TextField
              label={`Item ${i + 1}`}
              value={item}
              onChange={(v) => setData((d) => {
                const next = JSON.parse(JSON.stringify(d));
                next.petrol.manufacture[i] = v;
                return next;
              })}
            />
          )}
        />

        <TextAreaField label="Conclusion (en rouge)" rows={2} value={data.petrol?.conclusion} onChange={(v) => upd('petrol.conclusion', v)} />
      </SectionCard>

      {/* TRANSPORT */}
      <SectionCard title="Transport" description="Section fond dégradé bleu/vert.">
        <TextField label="Titre" value={data.transport?.title} onChange={(v) => upd('transport.title', v)} />
        <Repeater
          label="Paragraphes"
          items={data.transport?.paragraphs}
          onChange={(items) => upd('transport.paragraphs', items)}
          newItem={() => ''}
          renderItem={(item, update, i) => (
            <TextAreaField
              label={`Paragraphe ${i + 1}`}
              rows={3}
              value={item}
              onChange={(v) => setData((d) => {
                const next = JSON.parse(JSON.stringify(d));
                next.transport.paragraphs[i] = v;
                return next;
              })}
            />
          )}
        />
        <TextAreaField label="Message vert (bloc kiosque)" rows={3} value={data.transport?.kiosque} onChange={(v) => upd('transport.kiosque', v)} />
      </SectionCard>

      {/* WASTE */}
      <SectionCard title="Déchets plastiques" description="Section blanche avec bloc surligné.">
        <TextField label="Titre" value={data.waste?.title} onChange={(v) => upd('waste.title', v)} />
        <Repeater
          label="Paragraphes"
          items={data.waste?.paragraphs}
          onChange={(items) => upd('waste.paragraphs', items)}
          newItem={() => ''}
          renderItem={(item, update, i) => (
            <TextAreaField
              label={`Paragraphe ${i + 1}`}
              rows={3}
              value={item}
              onChange={(v) => setData((d) => {
                const next = JSON.parse(JSON.stringify(d));
                next.waste.paragraphs[i] = v;
                return next;
              })}
            />
          )}
        />

        <div className="p-4 rounded-2xl bg-sky-50/60 border border-sky-100 space-y-3">
          <p className="text-xs font-bold uppercase text-slate-600 tracking-wider">Encart chiffre (bloc sombre à droite)</p>
          <TextField label="Ligne 1 (gros chiffre)" value={data.waste?.highlight?.line1} onChange={(v) => upd('waste.highlight.line1', v)} />
          <TextField label="Ligne 2" value={data.waste?.highlight?.line2} onChange={(v) => upd('waste.highlight.line2', v)} />
          <TextField label="Ligne 3 (sous-titre en majuscules)" value={data.waste?.highlight?.line3} onChange={(v) => upd('waste.highlight.line3', v)} />
        </div>
      </SectionCard>

      {/* COST */}
      <SectionCard title="Coût économique" description="Section avec 2 tableaux de prix.">
        <TextField label="Titre" value={data.cost?.title} onChange={(v) => upd('cost.title', v)} />
        <TextAreaField label="Introduction" rows={2} value={data.cost?.intro} onChange={(v) => upd('cost.intro', v)} />

        <Repeater
          label="Prix moyen au litre (gauche)"
          items={data.cost?.prices}
          onChange={(items) => upd('cost.prices', items)}
          newItem={() => ({ type: '', price: '' })}
          renderItem={(item, update) => (
            <div className="grid sm:grid-cols-2 gap-3">
              <TextField label="Type" value={item.type} onChange={(v) => update({ type: v })} />
              <TextField label="Prix" value={item.price} onChange={(v) => update({ price: v })} />
            </div>
          )}
        />

        <TextField label="Sous-titre famille" hint="Ex : 500 L/an" value={data.cost?.familyIntro} onChange={(v) => upd('cost.familyIntro', v)} />
        <Repeater
          label="Coût annuel pour une famille (droite)"
          items={data.cost?.family}
          onChange={(items) => upd('cost.family', items)}
          newItem={() => ({ label: '', cost: '' })}
          renderItem={(item, update) => (
            <div className="grid sm:grid-cols-2 gap-3">
              <TextField label="Libellé" value={item.label} onChange={(v) => update({ label: v })} />
              <TextField label="Coût" value={item.cost} onChange={(v) => update({ cost: v })} />
            </div>
          )}
        />

        <TextAreaField label="Conclusion" rows={3} value={data.cost?.conclusion} onChange={(v) => upd('cost.conclusion', v)} />
      </SectionCard>

      {/* VIDEO */}
      <SectionCard title="Vidéos" description="Bloc sombre avec les vidéos illustratives.">
        <TextAreaField label="Texte d'introduction (avant les vidéos)" rows={3} value={data.videoIntro} onChange={(v) => upd('videoIntro', v)} />
        <Repeater
          label="Liste des vidéos"
          items={data.videos}
          onChange={(items) => upd('videos', items)}
          newItem={() => ({ number: '', title: '', source: '', url: '', thumbnail: '' })}
          renderItem={(item, update) => (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <TextField label="Numéro" value={item.number} onChange={(v) => update({ number: v })} />
                <TextField label="Source" value={item.source} onChange={(v) => update({ source: v })} />
              </div>
              <TextField label="Titre" value={item.title} onChange={(v) => update({ title: v })} />
              <TextField label="URL (YouTube ou mp4)" value={item.url} onChange={(v) => update({ url: v })} />
              <ImageField label="Miniature (thumbnail)" value={item.thumbnail} onChange={(v) => update({ thumbnail: v })} />
            </div>
          )}
        />
      </SectionCard>

      {/* FINAL CTA */}
      <SectionCard title="Bloc final (CTA)">
        <TextField label="Titre" value={data.finalCta?.title} onChange={(v) => upd('finalCta.title', v)} />
        <TextAreaField label="Description" rows={3} value={data.finalCta?.description} onChange={(v) => upd('finalCta.description', v)} />
      </SectionCard>
    </div>
  );
}
